/**
 * Shared route-badge display rules for search autocomplete and map stop labels.
 */

import type { GtfsRoute } from "../types/Gtfs.types";
import { dedupeRoutesByFranceBadge } from "./routeTypeFranceBadge.tools";

/** Route count above which the display truncates and shows an ellipsis pill. */
export const TRANSIT_ROUTE_ELLIPSIS_THRESHOLD = 6;

/** Maximum routes shown before the ellipsis pill when over {@link TRANSIT_ROUTE_ELLIPSIS_THRESHOLD}. */
export const TRANSIT_ROUTE_MAX_BEFORE_ELLIPSIS = 5;

/** Map stop label grid: four badges per row. */
export const STOP_LABEL_ROUTE_COLUMNS = 4;

/** Map stop label grid: at most two rows of correspondences. */
export const STOP_LABEL_ROUTE_MAX_ROWS = 2;

/** Maximum correspondence slots on a map stop label ({@link STOP_LABEL_ROUTE_COLUMNS} × {@link STOP_LABEL_ROUTE_MAX_ROWS}). */
export const STOP_LABEL_ROUTE_DISPLAY_MAX =
    STOP_LABEL_ROUTE_COLUMNS * STOP_LABEL_ROUTE_MAX_ROWS;

/** Route count above which map stop labels truncate and reserve the last slot for `...`. */
export const STOP_LABEL_ROUTE_ELLIPSIS_THRESHOLD = STOP_LABEL_ROUTE_DISPLAY_MAX;

/** Routes shown before the ellipsis when over {@link STOP_LABEL_ROUTE_ELLIPSIS_THRESHOLD}. */
export const STOP_LABEL_ROUTE_MAX_BEFORE_ELLIPSIS = STOP_LABEL_ROUTE_DISPLAY_MAX - 1;

/** Resolved route list for map stop label correspondence grids. */
export interface StopLabelTransitRoutesForDisplay {
    "displayedRoutes": GtfsRoute[];
    "showEllipsis":    boolean;
}

/** Resolved route list and layout flags for badge grids. */
export interface TransitRoutesForDisplay {
    "displayedRoutes":    GtfsRoute[];
    "showEllipsis":       boolean;
    "useThreeColumnGrid": boolean;
}

/**
 * Applies the shared slice / ellipsis / grid-column rules used in search autocomplete.
 *
 * @param routes Full route list for a stop.
 * @returns Routes to render, ellipsis flag, and whether to use a three-column grid.
 */
/**
 * Applies map stop label rules: up to eight routes (4×2), ellipsis when more than eight.
 *
 * @param routes Full route list for a stop.
 * @returns Routes to render and whether the last grid slot is an ellipsis pill.
 */
export function resolveStopLabelTransitRoutesForDisplay( routes: readonly GtfsRoute[] ): StopLabelTransitRoutesForDisplay {
    // France product badges deduplicate by icon first so duplicates do not consume display slots.
    const dedupedRoutes = dedupeRoutesByFranceBadge( routes );
    const showEllipsis = dedupedRoutes.length > STOP_LABEL_ROUTE_ELLIPSIS_THRESHOLD;
    const displayedRoutes = showEllipsis
        ? dedupedRoutes.slice( 0, STOP_LABEL_ROUTE_MAX_BEFORE_ELLIPSIS )
        : dedupedRoutes;

    return {
        displayedRoutes,
        showEllipsis,
    };
}

/**
 * Applies the shared slice / ellipsis / grid-column rules used in search autocomplete.
 *
 * @param routes Full route list for a stop.
 * @returns Routes to render, ellipsis flag, and whether to use a three-column grid.
 */
export function resolveTransitRoutesForDisplay( routes: readonly GtfsRoute[] ): TransitRoutesForDisplay {
    // France product badges deduplicate by icon first so duplicates do not consume display slots.
    const dedupedRoutes = dedupeRoutesByFranceBadge( routes );
    const showEllipsis = dedupedRoutes.length > TRANSIT_ROUTE_ELLIPSIS_THRESHOLD;
    const displayedRoutes = showEllipsis
        ? dedupedRoutes.slice( 0, TRANSIT_ROUTE_MAX_BEFORE_ELLIPSIS )
        : dedupedRoutes;

    return {
        displayedRoutes,
        showEllipsis,
        "useThreeColumnGrid": dedupedRoutes.length >= 3,
    };
}
